// Activity by hour as swarms of dots round a line, one row per country or weekday (after Bencho's dot swarm). Each
// row is a faint grid of dots; its own dots gather round the row's centre line, thick where that hour is busy and
// down to the single line where it's quiet. Run along it: the column under the pointer swells, a dashed line follows
// and the tag reads that hour. Point at a row and it has the chart to itself. With `zones`, the button walks time
// zones and each row slides to that zone's day (Local puts every country on its own clock).
//
//   x-data="uiSwarm({ rows: [{ label: 'Brazil', short: 'BR', color: 'var(--lo-b3)', v: [24 numbers or null] }],
//                     unit: 'videos', sum: true, zones: [{ name: 'UTC', shift: [0, 0] }, { name: 'Local time', shift: [-3, -4] }] })"
// For rows set later (a tool's result): x-effect="setRows(...)". `t` on a row gives its own tip text per hour.

const NS = 'http://www.w3.org/2000/svg';
const COLS = 24;
const PITCH = 8;    // px between dot centres down a row (across, the hours share the width)
const N = 5;        // dots per row, odd so there's a centre line
const GAP = 8;      // px between rows
const LABEL = 32;   // px for the row labels
const hh = (h) => `${String(h).padStart(2, '0')}:00`;
const fmt = (n) => Math.round(n).toLocaleString();
// a fixed hash, so a row's ragged edge is the same on every draw
const hash = (a, b, c) => { const x = Math.sin(a * 127.1 + b * 311.7 + c * 74.7) * 43758.5453; return x - Math.floor(x); };

export function uiSwarm(cfg = {}) {
    return {
        rows: cfg.rows ?? [],
        zone: 0,
        col: null,
        focus: null,
        tip: '',
        zones: cfg.zones ?? [],
        px: PITCH,
        init() {
            this.draw();
            this.$watch('zone', () => this.draw());
            let w = 0;
            new ResizeObserver(() => { const now = this.$refs.svg?.parentElement.clientWidth ?? 0; if (now && now !== w) { w = now; this.draw(); } }).observe(this.$root);
        },
        setRows(rows) { this.rows = rows ?? []; this.$nextTick(() => this.draw()); },
        nextZone() { if (this.zones.length) this.zone = (this.zone + 1) % this.zones.length; },
        zoneName() { return this.zones[this.zone]?.name ?? ''; },

        // a row's values on the current zone's clock
        shifted(r, i) {
            const s = this.zones[this.zone]?.shift?.[i] ?? 0;
            const at = (h) => ((h - s) % COLS + COLS) % COLS;
            return { v: Array.from({ length: COLS }, (_, h) => r.v?.[at(h)] ?? null), t: r.t ? Array.from({ length: COLS }, (_, h) => r.t[at(h)]) : null };
        },
        view() { return this.rows.map((r, i) => ({ ...r, ...this.shifted(r, i) })); },
        peak() {
            const v = this.view();
            let best = -1, at = 0;
            for (let c = 0; c < COLS; c++) {
                const sum = v.reduce((a, r) => a + (r.v[c] ?? 0), 0);
                if (sum > best) { best = sum; at = c; }
            }
            return at;
        },

        draw() {
            const svg = this.$refs.svg;
            if (!svg) return;
            const v = this.view();
            // each row is drawn against its own busiest hour: the point is its shape over the day, the tag has the numbers
            const rowH = N * PITCH;
            const W = Math.max(LABEL + COLS * PITCH, svg.parentElement.clientWidth);
            const px = (W - LABEL) / COLS;
            const sub = Math.max(1, Math.floor(px / PITCH));
            this.px = px;
            const H = Math.max(rowH, v.length * rowH + (v.length - 1) * GAP);
            svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
            svg.setAttribute('width', W);
            svg.setAttribute('height', H);
            svg.replaceChildren();
            const half = (N - 1) / 2;
            v.forEach((r, ri) => {
                const max = Math.max(1e-9, ...r.v.filter((x) => x !== null));
                const g = document.createElementNS(NS, 'g');
                g.setAttribute('class', 'ui-dsw-row');
                g.dataset.r = ri;
                g.style.setProperty('--c', r.color ?? 'var(--lo-b3)');
                const y0 = ri * (rowH + GAP);
                const label = document.createElementNS(NS, 'text');
                label.setAttribute('x', 0);
                label.setAttribute('y', y0 + rowH / 2 + 4);
                label.setAttribute('class', 'ui-dsw-lbl');
                label.textContent = r.short ?? r.label;
                g.appendChild(label);
                for (let c = 0; c < COLS; c++) {
                    const col = document.createElementNS(NS, 'g');
                    col.setAttribute('class', 'ui-dsw-col');
                    col.dataset.c = c;
                    col.style.setProperty('--d', `${Math.round((c / COLS) * 480)}ms`);
                    const val = r.v[c];
                    const reach = val === null ? -1 : (val / max) * half;
                    // an hour is as many dot columns as fit, so the field stays a dense grid at any width
                    for (let j = 0; j < sub; j++) {
                        for (let k = 0; k < N; k++) {
                            const d = Math.abs(k - half);
                            const on = val !== null && (d === 0 || d <= reach + (hash(c * sub + j, k, ri) - 0.5) * 0.9);
                            const dot = document.createElementNS(NS, 'circle');
                            dot.setAttribute('cx', LABEL + c * px + (j + 0.5) * (px / sub));
                            dot.setAttribute('cy', y0 + k * PITCH + PITCH / 2);
                            dot.setAttribute('r', 2.4);
                            dot.setAttribute('class', on ? 'ui-dsw-dot' : 'ui-dsw-wash');
                            col.appendChild(dot);
                        }
                    }
                    g.appendChild(col);
                }
                svg.appendChild(g);
            });
            this.$root.dataset.in = '1';
            this.place();
        },

        // pointer to column and row, in the svg's own units
        pick(e) {
            const svg = this.$refs.svg;
            const b = svg.getBoundingClientRect();
            const vb = svg.viewBox.baseVal;
            if (!b.width || !vb.width) return;
            const x = ((e.clientX - b.left) / b.width) * vb.width - LABEL;
            const y = ((e.clientY - b.top) / b.height) * vb.height;
            this.col = Math.max(0, Math.min(COLS - 1, Math.floor(x / this.px)));
            const rowH = N * PITCH;
            const ri = Math.floor(y / (rowH + GAP));
            this.focus = ri >= 0 && ri < this.rows.length && y - ri * (rowH + GAP) <= rowH ? ri : null;
            this.place();
        },
        leave() { this.col = null; this.focus = null; this.place(); },

        place() {
            const svg = this.$refs.svg;
            if (!svg || !this.rows.length) return;
            const c = this.col ?? this.peak();
            svg.querySelectorAll('.ui-dsw-col[data-on]').forEach((g) => g.removeAttribute('data-on'));
            if (this.col !== null) svg.querySelectorAll(`.ui-dsw-col[data-c="${c}"]`).forEach((g) => g.setAttribute('data-on', ''));
            svg.querySelectorAll('.ui-dsw-row').forEach((g) => g.toggleAttribute('data-hot', this.focus !== null && +g.dataset.r === this.focus));
            this.$root.toggleAttribute('data-focus', this.focus !== null);
            this.tip = this.tipFor(c);
            const b = svg.getBoundingClientRect();
            const vb = svg.viewBox.baseVal;
            const k = b.width / (vb.width || 1);
            const x = (LABEL + c * this.px + this.px / 2) * k;
            const line = this.$refs.line;
            line.style.height = `${b.height + 8}px`;
            line.style.transform = `translateX(${x}px)`;
            this.$nextTick(() => {
                const tag = this.$refs.tag;
                const halfW = tag.offsetWidth / 2;
                tag.style.transform = `translateX(${Math.max(0, Math.min(b.width - tag.offsetWidth, x - halfW))}px)`;
            });
        },
        tipFor(c) {
            const v = this.view();
            if (this.focus !== null) {
                const r = v[this.focus];
                if (r.t) return r.t[c] ?? '';
                return r.v[c] === null ? `${r.label} · ${hh(c)} · not enough ${cfg.unit ?? 'data'}` : `${r.label} · ${fmt(r.v[c])} ${cfg.unit ?? ''} at ${hh(c)}`;
            }
            const lead = v.reduce((a, r) => ((r.v[c] ?? -1) > (a?.v[c] ?? -1) ? r : a), null);
            const peak = this.col === null ? 'Peak ' : '';
            if (cfg.sum) {
                const sum = v.reduce((a, r) => a + (r.v[c] ?? 0), 0);
                return `${peak}${hh(c)} · ${fmt(sum)} ${cfg.unit ?? ''}${lead && v.length > 1 ? ` · ${lead.label} leads` : ''}`;
            }
            return lead?.t?.[c] ? `${peak}${lead.t[c]}` : `${peak}${hh(c)}`;
        },
    };
}
